import React, { useState, useRef } from 'react';
import {
  Sun, Moon, LogOut, LayoutDashboard, Settings, HelpCircle,
  Menu, History, Users, User, Phone, Mail, Lock, Eye, EyeOff,
  Check, AlertTriangle, Shield, CreditCard, Smartphone, X, Copy, Zap, Activity
} from 'lucide-react';
import { api } from '../api';
import './DashboardPage.css';
import './SettingsPage.css';

const LOGO_URL = 'https://acesso.ihubs.app/api/uploads/logo/acesso/logo-light-1773085624457.png';

const formatCpf = (cpf) =>
  (cpf || '').replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');

const formatPhoneInput = (value) => {
  const nums = value.replace(/\D/g, '').slice(0, 11);
  if (nums.length <= 2) return nums;
  if (nums.length <= 7) return `(${nums.slice(0, 2)}) ${nums.slice(2)}`;
  return `(${nums.slice(0, 2)}) ${nums.slice(2, 7)}-${nums.slice(7)}`;
};

function SettingsPage({ user, setUser, onLogout, theme, setTheme, setPage }) {
  const [activeTab, setActiveTab] = useState('profile');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Profile form
  const [profileForm, setProfileForm] = useState({
    name: user.name || '',
    email: user.email || '',
    phone: user.phone || '',
  });
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState('');
  const [profileError, setProfileError] = useState('');

  // Password form
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);

  // 2FA state
  const [tfaStep, setTfaStep] = useState(null); // null, 'setup', 'verify', 'disable'
  const [tfaQrCode, setTfaQrCode] = useState('');
  const [tfaSecret, setTfaSecret] = useState('');
  const [tfaCode, setTfaCode] = useState(['', '', '', '', '', '']);
  const [tfaLoading, setTfaLoading] = useState(false);
  const [tfaSuccess, setTfaSuccess] = useState('');
  const [tfaError, setTfaError] = useState('');
  const [tfaDisablePassword, setTfaDisablePassword] = useState('');
  const [showTfaDisablePw, setShowTfaDisablePw] = useState(false);
  const [secretCopied, setSecretCopied] = useState(false);
  const tfaInputRefs = useRef([]);

  const initials = (user.name || 'U').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  const isAdmin = user.role === 'admin' || user.role === 'supervisor';

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setProfileError('');
    setProfileSuccess('');
    setProfileLoading(true);
    try {
      const updated = await api.updateProfile({
        name: profileForm.name,
        email: profileForm.email || null,
        phone: profileForm.phone.replace(/\D/g, ''),
      });
      setUser(updated);
      setProfileSuccess('Perfil atualizado com sucesso!');
      setTimeout(() => setProfileSuccess(''), 3000);
    } catch (err) {
      setProfileError(err.error || 'Erro ao atualizar perfil');
    } finally {
      setProfileLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('As senhas não coincidem');
      return;
    }

    if (passwordForm.newPassword.length < 4) {
      setPasswordError('A nova senha deve ter no mínimo 4 caracteres');
      return;
    }

    setPasswordLoading(true);
    try {
      await api.changePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setPasswordSuccess('Senha alterada com sucesso!');
      setTimeout(() => setPasswordSuccess(''), 3000);
    } catch (err) {
      setPasswordError(err.error || 'Erro ao alterar senha');
    } finally {
      setPasswordLoading(false);
    }
  };

  // 2FA handlers
  const handleSetup2FA = async () => {
    setTfaError('');
    setTfaLoading(true);
    try {
      const data = await api.setup2FA();
      setTfaQrCode(data.qrCode);
      setTfaSecret(data.secret);
      setTfaStep('setup');
      setTfaCode(['', '', '', '', '', '']);
    } catch (err) {
      setTfaError(err.error || 'Erro ao configurar 2FA');
    } finally {
      setTfaLoading(false);
    }
  };

  const handleTfaDigitChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newCode = [...tfaCode];
    newCode[index] = value.slice(-1);
    setTfaCode(newCode);

    if (value && index < 5) {
      tfaInputRefs.current[index + 1]?.focus();
    }
  };

  const handleTfaKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !tfaCode[index] && index > 0) {
      tfaInputRefs.current[index - 1]?.focus();
    }
  };

  const handleTfaPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length > 0) {
      const newCode = [...tfaCode];
      for (let i = 0; i < 6; i++) {
        newCode[i] = pasted[i] || '';
      }
      setTfaCode(newCode);
      const focusIdx = Math.min(pasted.length, 5);
      tfaInputRefs.current[focusIdx]?.focus();
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    const code = tfaCode.join('');
    if (code.length !== 6) return;

    setTfaError('');
    setTfaLoading(true);
    try {
      await api.verify2FA(code);
      setUser({ ...user, totp_enabled: true });
      setTfaStep(null);
      setTfaSuccess('Autenticação em duas etapas ativada com sucesso!');
      setTimeout(() => setTfaSuccess(''), 4000);
    } catch (err) {
      setTfaError(err.error || 'Código inválido');
      setTfaCode(['', '', '', '', '', '']);
      setTimeout(() => tfaInputRefs.current[0]?.focus(), 100);
    } finally {
      setTfaLoading(false);
    }
  };

  const handleDisable2FA = async (e) => {
    e.preventDefault();
    if (!tfaDisablePassword) return;

    setTfaError('');
    setTfaLoading(true);
    try {
      await api.disable2FA(tfaDisablePassword);
      setUser({ ...user, totp_enabled: false });
      setTfaStep(null);
      setTfaDisablePassword('');
      setTfaSuccess('Autenticação em duas etapas desativada.');
      setTimeout(() => setTfaSuccess(''), 4000);
    } catch (err) {
      setTfaError(err.error || 'Erro ao desativar 2FA');
    } finally {
      setTfaLoading(false);
    }
  };

  const copySecret = () => {
    navigator.clipboard.writeText(tfaSecret);
    setSecretCopied(true);
    setTimeout(() => setSecretCopied(false), 2000);
  };

  const roleLabels = { admin: 'Administrador', supervisor: 'Supervisor', usuario: 'Usuário' };

  return (
    <div className={`dashboard-container ${theme}`}>
      {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />}

      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-top">
          <div className="sidebar-logo">
            <img src={LOGO_URL} alt="Logo" className="sidebar-logo-img" />
          </div>
          <nav className="sidebar-nav">
            <a href="#dashboard" className="nav-item" onClick={(e) => { e.preventDefault(); setPage('dashboard'); }}>
              <LayoutDashboard size={19} /><span>Dashboard</span>
            </a>
            {isAdmin && (
              <a href="#admin" className="nav-item" onClick={(e) => { e.preventDefault(); setPage('admin'); }}>
                <Users size={19} /><span>Admin</span>
              </a>
            )}
            {isAdmin && (
              <a href="#automation" className="nav-item" onClick={(e) => { e.preventDefault(); setPage('automation'); }}>
                <Zap size={19} /><span>Automacao IA</span>
              </a>
            )}
            {isAdmin && (
              <a href="#activity" className="nav-item" onClick={(e) => { e.preventDefault(); setPage('activity'); }}>
                <Activity size={19} /><span>Atividade</span>
              </a>
            )}
            <a href="#history" className="nav-item" onClick={(e) => { e.preventDefault(); setPage('history'); }}>
              <History size={19} /><span>Histórico</span>
            </a>
            <a href="#settings" className="nav-item active" onClick={() => setSidebarOpen(false)}>
              <Settings size={19} /><span>Configurações</span>
            </a>
            <a href="#help" className="nav-item">
              <HelpCircle size={19} /><span>Ajuda</span>
            </a>
          </nav>
        </div>
        <div className="sidebar-bottom">
          <button className="sidebar-theme-btn" onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}>
            {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
            <span>{theme === 'dark' ? 'Tema claro' : 'Tema escuro'}</span>
          </button>
          <div className="sidebar-user">
            <div className="user-avatar">{initials}</div>
            <div className="user-info">
              <span className="user-name">{user.name}</span>
              <span className="user-role">{user.role}</span>
            </div>
            <button className="logout-btn-sidebar" onClick={onLogout} title="Sair"><LogOut size={16} /></button>
          </div>
        </div>
      </aside>

      <div className="main-wrapper">
        <header className="topbar">
          <button className="menu-btn" onClick={() => setSidebarOpen(true)}><Menu size={20} /></button>
          <img src={LOGO_URL} alt="Logo" className="topbar-logo" />
          <div className="topbar-right">
            <button className="topbar-theme" onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}>
              {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            <div className="user-avatar small">{initials}</div>
          </div>
        </header>

        <main className="dash-main">
          <div className="page-header">
            <div>
              <p className="page-greeting">Minha Conta</p>
              <h1 className="page-title">Configurações</h1>
            </div>
          </div>

          {/* User Info Card */}
          <div className="settings-profile-card">
            <div className="settings-profile-avatar">{initials}</div>
            <div className="settings-profile-info">
              <span className="settings-profile-name">{user.name}</span>
              <span className="settings-profile-role">
                <Shield size={12} />
                {roleLabels[user.role] || user.role}
              </span>
            </div>
            <div className="settings-profile-cpf">
              <CreditCard size={14} />
              <span>{formatCpf(user.cpf)}</span>
            </div>
          </div>

          {/* Tabs */}
          <div className="settings-tabs">
            <button
              className={`settings-tab ${activeTab === 'profile' ? 'active' : ''}`}
              onClick={() => setActiveTab('profile')}
            >
              <User size={15} />
              <span>Dados Pessoais</span>
            </button>
            <button
              className={`settings-tab ${activeTab === 'security' ? 'active' : ''}`}
              onClick={() => setActiveTab('security')}
            >
              <Shield size={15} />
              <span>Segurança</span>
            </button>
            <button
              className={`settings-tab ${activeTab === 'appearance' ? 'active' : ''}`}
              onClick={() => setActiveTab('appearance')}
            >
              <Sun size={15} />
              <span>Aparência</span>
            </button>
          </div>

          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className="settings-section">
              <div className="settings-section-header">
                <h3><User size={17} /> Dados Pessoais</h3>
                <p>Atualize suas informações de contato</p>
              </div>

              {profileSuccess && <div className="settings-success"><Check size={14} /><span>{profileSuccess}</span></div>}
              {profileError && <div className="modal-error"><AlertTriangle size={14} /><span>{profileError}</span></div>}

              <form onSubmit={handleProfileSave} className="settings-form">
                <div className="settings-form-group">
                  <label>Nome completo</label>
                  <div className="input-with-icon">
                    <User size={16} className="field-icon" />
                    <input type="text" className="modal-input-styled" placeholder="Seu nome"
                      value={profileForm.name} required
                      onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })} />
                  </div>
                </div>

                <div className="settings-form-row">
                  <div className="settings-form-group">
                    <label>Telefone</label>
                    <div className="input-with-icon">
                      <Phone size={16} className="field-icon" />
                      <input type="text" className="modal-input-styled" placeholder="(00) 00000-0000"
                        value={formatPhoneInput(profileForm.phone)} required
                        onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })} />
                    </div>
                  </div>

                  <div className="settings-form-group">
                    <label>E-mail <span className="optional-tag">opcional</span></label>
                    <div className="input-with-icon">
                      <Mail size={16} className="field-icon" />
                      <input type="email" className="modal-input-styled" placeholder="email@exemplo.com"
                        value={profileForm.email}
                        onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })} />
                    </div>
                  </div>
                </div>

                <div className="settings-form-group readonly-group">
                  <label>CPF</label>
                  <div className="input-with-icon">
                    <CreditCard size={16} className="field-icon" />
                    <input type="text" className="modal-input-styled" value={formatCpf(user.cpf)} disabled />
                  </div>
                  <span className="readonly-hint">O CPF não pode ser alterado</span>
                </div>

                <button type="submit" className="settings-save-btn" disabled={profileLoading}>
                  {profileLoading ? <div className="spinner-sm" /> : <><Check size={16} /> Salvar alterações</>}
                </button>
              </form>
            </div>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <>
              {/* Password section */}
              <div className="settings-section">
                <div className="settings-section-header">
                  <h3><Lock size={17} /> Alterar Senha</h3>
                  <p>Mantenha sua conta segura com uma senha forte</p>
                </div>

                {passwordSuccess && <div className="settings-success"><Check size={14} /><span>{passwordSuccess}</span></div>}
                {passwordError && <div className="modal-error"><AlertTriangle size={14} /><span>{passwordError}</span></div>}

                <form onSubmit={handlePasswordChange} className="settings-form">
                  <div className="settings-form-group">
                    <label>Senha atual</label>
                    <div className="input-with-icon">
                      <Lock size={16} className="field-icon" />
                      <input type={showCurrentPw ? 'text' : 'password'} className="modal-input-styled"
                        placeholder="Digite sua senha atual" required
                        value={passwordForm.currentPassword}
                        onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })} />
                      <button type="button" className="field-toggle" onClick={() => setShowCurrentPw(!showCurrentPw)}>
                        {showCurrentPw ? <EyeOff size={15} /> : <Eye size={15} />}
                      </button>
                    </div>
                  </div>

                  <div className="settings-form-row">
                    <div className="settings-form-group">
                      <label>Nova senha</label>
                      <div className="input-with-icon">
                        <Lock size={16} className="field-icon" />
                        <input type={showNewPw ? 'text' : 'password'} className="modal-input-styled"
                          placeholder="Mínimo 4 caracteres" required minLength={4}
                          value={passwordForm.newPassword}
                          onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })} />
                        <button type="button" className="field-toggle" onClick={() => setShowNewPw(!showNewPw)}>
                          {showNewPw ? <EyeOff size={15} /> : <Eye size={15} />}
                        </button>
                      </div>
                    </div>

                    <div className="settings-form-group">
                      <label>Confirmar nova senha</label>
                      <div className="input-with-icon">
                        <Lock size={16} className="field-icon" />
                        <input type={showNewPw ? 'text' : 'password'} className="modal-input-styled"
                          placeholder="Repita a nova senha" required minLength={4}
                          value={passwordForm.confirmPassword}
                          onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })} />
                      </div>
                    </div>
                  </div>

                  <button type="submit" className="settings-save-btn" disabled={passwordLoading}>
                    {passwordLoading ? <div className="spinner-sm" /> : <><Lock size={16} /> Alterar senha</>}
                  </button>
                </form>
              </div>

              {/* 2FA Section */}
              <div className="settings-section">
                <div className="settings-section-header">
                  <h3><Smartphone size={17} /> Autenticação em Duas Etapas (2FA)</h3>
                  <p>Adicione uma camada extra de segurança usando o Google Authenticator</p>
                </div>

                {tfaSuccess && <div className="settings-success"><Check size={14} /><span>{tfaSuccess}</span></div>}
                {tfaError && !tfaStep && <div className="modal-error"><AlertTriangle size={14} /><span>{tfaError}</span></div>}

                {/* Status card */}
                <div className={`tfa-status-card ${user.totp_enabled ? 'enabled' : 'disabled'}`}>
                  <div className="tfa-status-icon">
                    <Shield size={20} />
                  </div>
                  <div className="tfa-status-info">
                    <span className="tfa-status-title">
                      {user.totp_enabled ? '2FA Ativado' : '2FA Desativado'}
                    </span>
                    <span className="tfa-status-desc">
                      {user.totp_enabled
                        ? 'Sua conta está protegida com verificação em duas etapas'
                        : 'Ative para proteger sua conta com verificação em duas etapas'
                      }
                    </span>
                  </div>
                  <div className={`tfa-status-badge ${user.totp_enabled ? 'on' : 'off'}`}>
                    {user.totp_enabled ? 'Ativo' : 'Inativo'}
                  </div>
                </div>

                {/* No active step - show toggle buttons */}
                {!tfaStep && (
                  <div className="tfa-actions">
                    {!user.totp_enabled ? (
                      <button className="tfa-enable-btn" onClick={handleSetup2FA} disabled={tfaLoading}>
                        {tfaLoading ? <div className="spinner-sm" /> : <><Smartphone size={16} /> Ativar 2FA</>}
                      </button>
                    ) : (
                      <button className="tfa-disable-btn" onClick={() => { setTfaStep('disable'); setTfaError(''); }}>
                        <X size={16} /> Desativar 2FA
                      </button>
                    )}
                  </div>
                )}

                {/* Setup step - QR Code */}
                {tfaStep === 'setup' && (
                  <div className="tfa-setup-panel">
                    <div className="tfa-setup-steps">
                      <div className="tfa-step">
                        <div className="tfa-step-num">1</div>
                        <div className="tfa-step-text">
                          <span className="tfa-step-title">Instale o aplicativo</span>
                          <span className="tfa-step-desc">Baixe o Google Authenticator ou outro app compatível com TOTP</span>
                        </div>
                      </div>
                      <div className="tfa-step">
                        <div className="tfa-step-num">2</div>
                        <div className="tfa-step-text">
                          <span className="tfa-step-title">Escaneie o QR Code</span>
                          <span className="tfa-step-desc">Abra o app e escaneie o código abaixo</span>
                        </div>
                      </div>
                    </div>

                    <div className="tfa-qr-container">
                      <div className="tfa-qr-wrap">
                        <img src={tfaQrCode} alt="QR Code 2FA" className="tfa-qr-img" />
                      </div>

                      <div className="tfa-manual-key">
                        <span className="tfa-manual-label">Ou insira o código manualmente:</span>
                        <div className="tfa-secret-row">
                          <code className="tfa-secret-code">{tfaSecret}</code>
                          <button type="button" className="tfa-copy-btn" onClick={copySecret} title="Copiar">
                            {secretCopied ? <Check size={14} /> : <Copy size={14} />}
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="tfa-step" style={{ marginTop: '8px' }}>
                      <div className="tfa-step-num">3</div>
                      <div className="tfa-step-text">
                        <span className="tfa-step-title">Confirme o código</span>
                        <span className="tfa-step-desc">Digite o código de 6 dígitos gerado pelo app</span>
                      </div>
                    </div>

                    {tfaError && <div className="modal-error"><AlertTriangle size={14} /><span>{tfaError}</span></div>}

                    <form onSubmit={handleVerify2FA}>
                      <div className="tfa-code-inputs" onPaste={handleTfaPaste}>
                        {tfaCode.map((digit, i) => (
                          <React.Fragment key={i}>
                            <input
                              ref={(el) => (tfaInputRefs.current[i] = el)}
                              type="text"
                              inputMode="numeric"
                              maxLength={1}
                              className="tfa-code-digit"
                              value={digit}
                              onChange={(e) => handleTfaDigitChange(i, e.target.value)}
                              onKeyDown={(e) => handleTfaKeyDown(i, e)}
                            />
                            {i === 2 && <span className="tfa-code-sep" />}
                          </React.Fragment>
                        ))}
                      </div>

                      <div className="tfa-setup-actions">
                        <button type="button" className="tfa-cancel-btn" onClick={() => { setTfaStep(null); setTfaError(''); }}>
                          Cancelar
                        </button>
                        <button type="submit" className="settings-save-btn" disabled={tfaLoading || tfaCode.some(d => d === '')}>
                          {tfaLoading ? <div className="spinner-sm" /> : <><Check size={16} /> Ativar 2FA</>}
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {/* Disable step */}
                {tfaStep === 'disable' && (
                  <div className="tfa-setup-panel">
                    <div className="tfa-disable-warning">
                      <AlertTriangle size={18} />
                      <div>
                        <span className="tfa-warn-title">Tem certeza que deseja desativar?</span>
                        <span className="tfa-warn-desc">Sua conta ficará menos protegida sem a verificação em duas etapas.</span>
                      </div>
                    </div>

                    {tfaError && <div className="modal-error"><AlertTriangle size={14} /><span>{tfaError}</span></div>}

                    <form onSubmit={handleDisable2FA} className="settings-form">
                      <div className="settings-form-group">
                        <label>Confirme sua senha para desativar</label>
                        <div className="input-with-icon">
                          <Lock size={16} className="field-icon" />
                          <input type={showTfaDisablePw ? 'text' : 'password'} className="modal-input-styled"
                            placeholder="Digite sua senha" required
                            value={tfaDisablePassword}
                            onChange={(e) => setTfaDisablePassword(e.target.value)} />
                          <button type="button" className="field-toggle" onClick={() => setShowTfaDisablePw(!showTfaDisablePw)}>
                            {showTfaDisablePw ? <EyeOff size={15} /> : <Eye size={15} />}
                          </button>
                        </div>
                      </div>

                      <div className="tfa-setup-actions">
                        <button type="button" className="tfa-cancel-btn" onClick={() => { setTfaStep(null); setTfaError(''); setTfaDisablePassword(''); }}>
                          Cancelar
                        </button>
                        <button type="submit" className="tfa-disable-confirm-btn" disabled={tfaLoading || !tfaDisablePassword}>
                          {tfaLoading ? <div className="spinner-sm" /> : <><X size={16} /> Desativar 2FA</>}
                        </button>
                      </div>
                    </form>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Appearance Tab */}
          {activeTab === 'appearance' && (
            <div className="settings-section">
              <div className="settings-section-header">
                <h3><Sun size={17} /> Aparência</h3>
                <p>Personalize a aparência do sistema</p>
              </div>

              <div className="theme-selector">
                <button
                  className={`theme-option ${theme === 'dark' ? 'active' : ''}`}
                  onClick={() => setTheme('dark')}
                >
                  <div className="theme-preview dark-preview">
                    <div className="preview-sidebar" />
                    <div className="preview-content">
                      <div className="preview-line" />
                      <div className="preview-line short" />
                      <div className="preview-cards">
                        <div className="preview-card" />
                        <div className="preview-card" />
                      </div>
                    </div>
                  </div>
                  <div className="theme-option-info">
                    <Moon size={14} />
                    <span>Tema Escuro</span>
                  </div>
                  {theme === 'dark' && <div className="theme-check"><Check size={14} /></div>}
                </button>

                <button
                  className={`theme-option ${theme === 'light' ? 'active' : ''}`}
                  onClick={() => setTheme('light')}
                >
                  <div className="theme-preview light-preview">
                    <div className="preview-sidebar" />
                    <div className="preview-content">
                      <div className="preview-line" />
                      <div className="preview-line short" />
                      <div className="preview-cards">
                        <div className="preview-card" />
                        <div className="preview-card" />
                      </div>
                    </div>
                  </div>
                  <div className="theme-option-info">
                    <Sun size={14} />
                    <span>Tema Claro</span>
                  </div>
                  {theme === 'light' && <div className="theme-check"><Check size={14} /></div>}
                </button>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

export default SettingsPage;
